/**
 * Каталог виджетов New Tab Studio.
 *
 * Один источник правды для:
 *  - лендингов /widgets/:slug и хаба /widgets;
 *  - перелинковки (related);
 *  - sitemap.xml;
 *  - JSON-LD.
 *
 * Правило: у каждого виджета уникальные тексты. Шаблонные "mad-libs" абзацы
 * (подстановка названия в один и тот же текст) не добавляем — это прямой путь
 * под Scaled Content Abuse.
 *
 * @see docs/seo/SEO-STRATEGY.md
 * @see docs/widgets.md
 */

import time from './catalog/time';
import productivity from './catalog/productivity';
import navigation from './catalog/navigation';
import design from './catalog/design';
import v4 from './catalog/v4';
import { isReleased } from './release';

const widgets = [...time, ...productivity, ...navigation, ...design, ...v4];

/**
 * Когда в последний раз менялись тексты каталога.
 *
 * Уходит в <lastmod> карты сайта, поэтому должно быть настоящей датой
 * правки, а не датой сборки. Карта, которая каждый день сообщает «всё
 * изменилось сегодня», просто перестаёт учитываться.
 *
 * Поднимать руками при заметной правке текстов.
 */
export const CATALOG_UPDATED = '2026-09-30';

/** Порядок категорий на странице-хабе */
export const CATEGORIES = [
	{
		key: 'Time',
		title: 'Time & deadlines',
		intro:
			'Countdowns, clocks and dates. The widgets that put a number on how much time is left.',
	},
	{
		key: 'Productivity',
		title: 'Tasks & habits',
		intro:
			'Lists, notes, timers and trackers for the work itself — all editable straight from the tab.',
	},
	{
		key: 'Navigation',
		title: 'Navigation & search',
		intro:
			'Everything that gets you off this page and onto the one you actually wanted.',
	},
	{
		key: 'Content',
		title: 'News, facts & puzzles',
		intro:
			'Something to read or solve while you decide what to open next: headlines, a fact you didn’t know, a chess puzzle.',
	},
	{
		key: 'Design',
		title: 'Design & content',
		intro:
			'Text, images, shapes and backgrounds — the layer that turns a pile of widgets into a page.',
	},
];

/**
 * Все виджеты каталога, включая ещё не открытые для поисковика.
 * Для всего, что видит робот, нужен getPublicWidgets().
 *
 * @returns {Array}
 */
export function getWidgets() {
	return widgets;
}

/**
 * Виджеты, открытые для поисковика на данный момент: это то, что показывает
 * хаб, что уходит в sitemap.xml и на что мы линкуемся.
 *
 * @param {number|Date} [now]
 * @returns {Array}
 * @see ./release.js
 */
export function getPublicWidgets(now) {
	return widgets.filter(w => isReleased(w.slug, now));
}

/**
 * @param {string} slug
 * @returns {Object|undefined}
 */
export function getWidget(slug) {
	return widgets.find(w => w.slug === slug);
}

/**
 * Виджеты категории. По умолчанию только открытые — функция используется
 * для отрисовки хаба; весь каталог достаётся через { all: true }.
 *
 * @param {string} category ключ из CATEGORIES
 * @param {{all?: boolean, now?: number|Date}} [options]
 * @returns {Array}
 */
export function getWidgetsByCategory(category, options = {}) {
	const list = options.all ? widgets : getPublicWidgets(options.now);

	return list.filter(w => w.category === category);
}

/**
 * Виджеты для блока перелинковки. Если related не задан или ссылается
 * на несуществующий slug — молча пропускаем, чтобы не ронять страницу.
 * Ещё не открытые страницы отсюда тоже убираем: иначе робот дойдёт до них
 * по ссылке раньше срока.
 *
 * @param {Object} widget
 * @param {number|Date} [now]
 * @returns {Array}
 */
export function getRelated(widget, now) {
	return (widget.related || [])
		.map(getWidget)
		.filter(Boolean)
		.filter(w => isReleased(w.slug, now));
}

/**
 * URL каталога для sitemap.xml — только открытые страницы.
 *
 * @param {number|Date} [now]
 * @returns {Array<string>}
 */
export function getWidgetUrls(now) {
	return [
		'/widgets',
		...getPublicWidgets(now).map(w => `/widgets/${w.slug}`),
	];
}

export default widgets;

/**
 * className виджета из appJSON -> slug лендинга.
 *
 * Строится из самого каталога, поэтому не может разъехаться с ним:
 * добавили виджету поле model — он тут же начал узнаваться в темах.
 *
 * Собирается один раз при загрузке модуля: тем на сайте тысячи, и
 * пересобирать карту на каждый рендер страницы темы незачем.
 */
const MODEL_TO_SLUG = widgets.reduce((map, widget) => {
	if (widget.model) {
		return { ...map, [widget.model]: widget.slug };
	}

	return map;
}, {});

/**
 * Какие виджеты стоят в теме — по её appJSON.
 *
 * Нужно странице темы: тысячи таких страниц, и каждая может дать
 * осмысленную ссылку в каталог. Возвращает уникальные виджеты в порядке
 * каталога, а не в порядке появления в теме, чтобы список выглядел
 * одинаково у похожих тем.
 *
 * @param {Object} appJSON разобранный appJSON темы
 * @param {number|Date} [now]
 * @returns {Array} виджеты каталога, уже открытые для посетителя
 */
export function getWidgetsInTheme(appJSON, now) {
	const inTheme =
		appJSON && appJSON.widgets && Array.isArray(appJSON.widgets.widgets)
			? appJSON.widgets.widgets
			: [];

	const slugs = new Set(
		inTheme.map(widget => MODEL_TO_SLUG[widget.className]).filter(Boolean),
	);

	return getPublicWidgets(now).filter(widget => slugs.has(widget.slug));
}
